/**
 * Tap picking by where things are DRAWN (QOL backlog #5). A tower is tall: its roof is drawn over the cell behind it,
 * so picking by the ground cell under the finger hit the neighbour behind a tower whenever the player tapped its
 * roof or upper body. These pick in screen pixels against points on the drawn body instead. Pure maths: the level
 * supplies the projection.
 */

export interface ScreenPoint { readonly x: number; readonly y: number }
interface Vec3 { readonly x: number; readonly y: number; readonly z: number }
/** A world box a model occupies at rest (a THREE.Box3 fits). */
export interface DrawnBox { readonly min: Vec3; readonly max: Vec3 }

/**
 * Of `items`, the one with a drawn point nearest the screen point `(x, y)`, within `reachPx`; null if none is. Each
 * item may offer several points (a tower's foot, middle and upper body), and its nearest one counts.
 */
export function nearestDrawn<T>(x: number, y: number, items: readonly T[], pointsOf: (item: T) => readonly ScreenPoint[], reachPx: number): T | null {
  let best: T | null = null, bestD = reachPx * reachPx;
  for (const it of items) {
    for (const p of pointsOf(it)) {
      const d = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d < bestD) { bestD = d; best = it; }
    }
  }
  return best;
}

/**
 * Heights up a tower's drawn box, as shares of its height, that a tap is measured against besides its foot: the
 * middle of the body and high on it, under the roof. Not the very top, where a spire or flag is too thin to aim at
 * and would reach well into the row behind.
 */
export const TowerPickHeights: readonly number[] = [0.5, 0.8];

/**
 * A tower's pick points on screen: `foot` (its cell centre on the ground, already projected) and, over the middle of
 * its drawn box, a point at each of TowerPickHeights. The box's middle rather than the cell's, because the fortress
 * stands behind its gate cell.
 */
export function towerPickPoints(foot: ScreenPoint, box: DrawnBox | null, project: (x: number, y: number, z: number) => ScreenPoint): ScreenPoint[] {
  if (box === null || !(box.max.y > box.min.y)) return [foot];
  const cx = (box.min.x + box.max.x) / 2, cz = (box.min.z + box.max.z) / 2, h = box.max.y - box.min.y;
  return [foot, ...TowerPickHeights.map((k) => project(cx, box.min.y + h * k, cz))];
}
